/**
 * W2C-1.6.1 — Message Templates admin panel.
 *
 * Full CRUD for message_templates including workflow eligibility fields:
 *   audience, purpose, approved, provider_route_id, compliance_notes.
 *
 * Rendered both by:
 *   /templates route (pages/MessageTemplates.jsx wrapper)
 *   Settings → Templates tab
 *
 * DOES NOT send SMS/email. DOES NOT touch Twilio/SendGrid. DOES NOT
 * execute action_runs. Pure DB-level template administration.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    Plus, Copy, Archive, ArchiveRestore, Trash2, Eye, Send, Edit3,
    ShieldCheck, Info, ExternalLink, Bug, RefreshCw, Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const SUPPORTED_VARIABLES = ["first_name", "last_name", "phone", "email", "lead_type", "campaign_name"];
const EMAIL_DTD_KEYS = ["first_name", "last_name", "email", "phone", "campaign_name"];

const AUDIENCE_OPTIONS = [
    { value: "lead", label: "Lead" },
    { value: "client", label: "Client" },
    { value: "both", label: "Both (Lead + Client)" },
];
const PURPOSE_OPTIONS = [
    { value: "lead_outreach", label: "Lead outreach (campaigns)" },
    { value: "client_workflow", label: "Client workflow automation" },
    { value: "manual_client_message", label: "Manual client message (ad-hoc)" },
    { value: "poi_request", label: "POI request" },
    { value: "other", label: "Other" },
];

const empty = (channel = "sms") => ({
    template_key: "", template_name: "", channel, category: "",
    body: "", subject: "",
    sendgrid_template_id: "", from_email: "", from_name: "",
    // W2C-1.7.1 — default new email templates to Local CaseFlow mode.
    email_send_mode: channel === "email" ? "local" : null,
    active: true,
    // W2C-1.6.1 workflow eligibility defaults.
    audience: null, purpose: null, approved: null,
    provider_route_id: null, provider_route_name: null,
    compliance_notes: "",
    // P1B.1 — SMS-only sender configuration. Default to messaging_service
    // so existing behaviour is preserved on new templates.
    send_method: channel === "sms" ? "messaging_service" : null,
    exact_from_number: null,
    exact_from_number_sid: null,
});

function isPlaceholderSendgridId(id) {
    if (!id) return true;
    const u = id.toUpperCase();
    return !id.startsWith("d-") || u.includes("DEMO") || u.includes("REPLACE-ME");
}

/**
 * Evaluate whether a template will appear in the Workflow SMS/Email picker.
 * Mirrors backend /_meta/picker-diagnostics logic so the editor shows the
 * same answer the picker sees. `route` may be null when no route is linked.
 *
 * 2026-02 P0/P1 — Email templates NO LONGER require a Provider Route.
 * The route/MG SID model applies only to SMS. For email, sender identity
 * lives on the template itself (sendgrid_template_id / subject / from_email
 * / from_name) with a global-settings fallback at send time.
 */
function evaluateEligibility(row, route) {
    const channel = row.channel;
    const routeChannelOk = route && route.channel === channel;
    const checks = [
        {
            key: "channel", label: `Channel is ${channel === "sms" ? "SMS" : "Email"}`,
            ok: channel === "sms" || channel === "email",
        },
        {
            key: "audience", label: "Audience = Client (or Both)",
            ok: row.audience === "client" || row.audience === "both",
        },
        {
            key: "purpose", label: "Purpose = Client workflow automation",
            ok: row.purpose === "client_workflow",
        },
        { key: "active", label: "Active", ok: !!row.active },
        { key: "approved", label: "Approved", ok: row.approved === true },
    ];
    if (channel === "sms") {
        checks.push(
            {
                key: "route_linked", label: "Provider Route selected",
                ok: !!row.provider_route_id,
            },
            {
                key: "route_active", label: "Provider Route is active",
                ok: !!route && route.active !== false,
            },
            {
                key: "route_channel", label: `Provider Route channel matches (${channel})`,
                ok: !!routeChannelOk,
            },
            {
                key: "route_msid", label: "Route has a Twilio MG… Messaging Service SID",
                ok: !!(route && (route.twilio_messaging_service_sid || "").startsWith("MG")),
            },
        );
    }
    const passed = checks.every(c => c.ok);
    return { passed, checks };
}

/**
 * W2C-1.7 — Rendered Preview panel. Shows the merged body with resolved
 * placeholders substituted in, and highlights unresolved placeholders in
 * amber. The preview uses hard-coded SAMPLE data — the notice under the
 * pane makes that explicit.
 */
function RenderedPreview({ preview, channel }) {
    if (!preview) return null;
    const body = preview.body || {};
    const subject = preview.subject;
    const hasBody = !!(body.rendered || body.placeholders?.length);
    if (!hasBody && !subject) return null;

    // Highlight unresolved placeholders inside the rendered string by
    // wrapping any `{token}` / `{{token}}` still present in a colored span.
    const highlightUnresolved = (text, unresolved) => {
        if (!unresolved || unresolved.length === 0) return text;
        const parts = [];
        // Regex covers both {tok} and {{tok}}.
        const re = /\{\{?\s*([a-zA-Z0-9_]+)\s*\}?\}/g;
        let last = 0;
        let m;
        while ((m = re.exec(text)) !== null) {
            if (unresolved.includes(m[1])) {
                if (m.index > last) parts.push(text.slice(last, m.index));
                parts.push(
                    <span key={m.index} className="bg-amber-200/70 text-amber-900 rounded px-0.5 font-mono" data-testid={`unresolved-${m[1]}`}>
                        {m[0]}
                    </span>,
                );
                last = m.index + m[0].length;
            }
        }
        if (last < text.length) parts.push(text.slice(last));
        return parts;
    };

    return (
        <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-3 space-y-2" data-testid="rendered-preview">
            <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-sky-900">Rendered Preview</div>
                {body.has_unresolved ? (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="preview-has-unresolved">
                        {body.unresolved.length} unresolved
                    </span>
                ) : (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800" data-testid="preview-clean">
                        Clean
                    </span>
                )}
            </div>
            {channel === "email" && subject && (
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Subject</div>
                    <div className="text-sm text-slate-900 mt-0.5" data-testid="preview-rendered-subject">
                        {highlightUnresolved(subject.rendered || "", subject.unresolved || [])}
                    </div>
                </div>
            )}
            {hasBody && (
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Body</div>
                    <pre className="mt-0.5 whitespace-pre-wrap break-words text-sm text-slate-900 font-mono" data-testid="preview-rendered-body">
                        {highlightUnresolved(body.rendered || "", body.unresolved || [])}
                    </pre>
                </div>
            )}
            {body.has_unresolved && (
                <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="preview-unresolved-warning">
                    <span className="font-medium">Heads up:</span> the highlighted fields will block automated sends in W2C-2 until resolved. Fix the merge names or map new aliases on the backend.
                </div>
            )}
            <div className="text-[10px] text-slate-500" data-testid="preview-notice">
                Preview uses sample data. Final messages render with the actual client&apos;s information at send time.
            </div>
        </div>
    );
}

export default function MessageTemplatesPanel() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("active"); // active | archived
    const [channelFilter, setChannelFilter] = useState("all"); // all | sms | email
    const [eligibilityFilter, setEligibilityFilter] = useState("all"); // all | workflow_ready
    const [routes, setRoutes] = useState([]);
    const [editor, setEditor] = useState(null);     // { mode: "create"|"edit", row }
    const [confirm, setConfirm] = useState(null);
    const [previewing, setPreviewing] = useState(null);
    const [testTarget, setTestTarget] = useState({ open: false, dest: "", row: null, busy: false, result: null, sampleFirstName: "" });
    const [diag, setDiag] = useState(null); // { picker, ... rows[] }
    // W2C-1.7 — live rendered preview of the editor draft body + subject.
    const [renderPreview, setRenderPreview] = useState(null);
    // W2C-1.7.1 — cached SendGrid dynamic templates for the dropdown.
    const [sgTemplates, setSgTemplates] = useState([]);
    const [sgSyncing, setSgSyncing] = useState(false);
    const [showAdvancedId, setShowAdvancedId] = useState(false);

    const routeMap = useMemo(() => {
        const m = {};
        for (const r of routes) m[r.id] = r;
        return m;
    }, [routes]);

    const load = useCallback(async () => {
        const params = { include_archived: filter === "archived" };
        if (channelFilter !== "all") params.channel = channelFilter;
        try {
            const [tplRes, routeRes] = await Promise.all([
                api.get("/message-templates", { params }),
                api.get("/messaging-routes"),
            ]);
            let list = filter === "archived"
                ? tplRes.data.filter(t => t.archived)
                : tplRes.data.filter(t => !t.archived);
            setRoutes(routeRes.data || []);
            if (eligibilityFilter === "workflow_ready") {
                const rMap = {};
                for (const r of routeRes.data || []) rMap[r.id] = r;
                list = list.filter(t => evaluateEligibility(t, rMap[t.provider_route_id]).passed);
            }
            setItems(list);
        } catch (e) { toast.error(formatApiError(e)); }
    }, [filter, channelFilter, eligibilityFilter]);
    useEffect(() => { load(); }, [load]);

    // W2C-1.7.1 — lazy-load the cached SendGrid dynamic templates when the
    // editor opens for an email template (any mode). Never triggers a live
    // SendGrid API call on its own.
    useEffect(() => {
        if (!editor || editor.row.channel !== "email") return;
        (async () => {
            try {
                const { data } = await api.get("/sendgrid/templates");
                setSgTemplates(data || []);
            } catch (e) {
                console.error("[MessageTemplatesPanel] SG cache load failed", e?.response?.status || e?.message);
            }
        })();
    }, [editor?.row?.channel, editor]);

    const syncSendGrid = async () => {
        setSgSyncing(true);
        try {
            const { data } = await api.post("/sendgrid/templates/sync");
            toast.success(`Synced ${data.fetched} SendGrid templates`);
            const { data: cached } = await api.get("/sendgrid/templates");
            setSgTemplates(cached || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSgSyncing(false);
        }
    };

    // W2C-1.7 — debounced live-preview of the editor body/subject.
    useEffect(() => {
        if (!editor) { setRenderPreview(null); return; }
        const body = editor.row.body || "";
        const subject = editor.row.channel === "email" ? (editor.row.subject || "") : "";
        if (!body && !subject) { setRenderPreview(null); return; }
        const handle = setTimeout(async () => {
            try {
                const { data } = await api.post("/message-templates/preview-body", { body, subject });
                setRenderPreview(data);
            } catch (e) {
                // Preview failures are non-fatal — the editor still saves.
                console.error("[MessageTemplatesPanel] preview failed", e?.response?.status || e?.message);
                setRenderPreview(null);
            }
        }, 250);
        return () => clearTimeout(handle);
    }, [editor?.row?.body, editor?.row?.subject, editor?.row?.channel, editor]);

    const runDiagnostics = async (channel) => {
        try {
            const { data } = await api.get("/message-templates/_meta/picker-diagnostics", {
                params: { picker: channel === "sms" ? "workflow_sms" : "workflow_email" },
            });
            setDiag(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const save = async () => {
        const r = editor.row;
        if (!r.template_key.trim() || !r.template_name.trim()) {
            toast.error("Template key and name are required");
            return;
        }
        if (r.channel === "sms" && !r.body.trim()) {
            toast.error("SMS templates require a body");
            return;
        }
        try {
            const selectedRoute = r.provider_route_id ? routeMap[r.provider_route_id] : null;
            const isSms = r.channel === "sms";
            // P1B.1 — preserve the row's actual send_method. Legacy templates
            // load with `null` and must round-trip as `null` so they don't
            // suddenly require a Messaging Route on save. New templates get
            // "messaging_service" from `empty()` so the explicit UX enforces
            // route selection.
            const sendMethod = isSms ? (r.send_method ?? null) : null;
            const payload = {
                template_key: r.template_key.trim(),
                template_name: r.template_name.trim(),
                channel: r.channel,
                category: (r.category || "").trim() || null,
                body: r.channel === "sms" ? r.body : "",
                subject: r.channel === "email" ? (r.subject || null) : null,
                sendgrid_template_id: r.channel === "email" ? (r.sendgrid_template_id || null) : null,
                email_send_mode: r.channel === "email" ? (r.email_send_mode || "local") : null,
                from_email: r.channel === "email" ? (r.from_email || null) : null,
                from_name: r.channel === "email" ? (r.from_name || null) : null,
                active: r.active,
                // W2C-1.6.1 workflow eligibility.
                audience: r.audience || null,
                purpose: r.purpose || null,
                approved: r.approved === true ? true : r.approved === false ? false : null,
                provider_route_id: isSms ? (r.provider_route_id || null) : null,
                provider_route_name: isSms ? (selectedRoute ? selectedRoute.name : null) : null,
                compliance_notes: (r.compliance_notes || "").trim() || null,
                // P1B.1 — SMS sender configuration. Always send explicitly so
                // mode switches (Messaging Service ↔ Exact From Number) actually
                // persist. Backend clears the opposing fields based on mode.
                send_method: sendMethod,
                exact_from_number: isSms && sendMethod === "exact_from_number"
                    ? (r.exact_from_number || null)
                    : null,
                exact_from_number_sid: isSms && sendMethod === "exact_from_number"
                    ? (r.exact_from_number_sid || null)
                    : null,
            };
            if (editor.mode === "create") await api.post("/message-templates", payload);
            else await api.put(`/message-templates/${r.id}`, payload);
            toast.success("Template saved");
            setEditor(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const duplicate = async (row) => {
        try {
            await api.post(`/message-templates/${row.id}/duplicate`);
            toast.success("Duplicated");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const archive = async (row, next) => {
        try {
            await api.post(`/message-templates/${row.id}/${next ? "archive" : "unarchive"}`);
            toast.success(next ? "Archived" : "Restored");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (row) => {
        try {
            await api.delete(`/message-templates/${row.id}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const preview = async (row) => {
        try {
            const { data } = await api.post(`/message-templates/${row.id}/preview`, { variables: {} });
            setPreviewing(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const sendTest = async () => {
        if (!testTarget.dest.trim()) {
            toast.error(testTarget.row?.channel === "email" ? "Recipient email is required" : "Recipient phone is required");
            return;
        }
        setTestTarget(t => ({ ...t, busy: true }));
        try {
            const isSms = testTarget.row.channel === "sms";
            const url = isSms ? "/settings/test-sms" : "/settings/test-email";
            const fname = (testTarget.sampleFirstName || "").trim();
            let leadIdForEmail = null;
            if (!fname && !isSms) {
                try {
                    const { data: leads } = await api.get("/leads", { params: { page_size: 1 } });
                    if (leads?.items?.length) leadIdForEmail = leads.items[0].id;
                } catch { /* ignore */ }
            }
            const variables = fname
                ? { first_name: fname, last_name: "", email: isSms ? "" : testTarget.dest,
                    phone: isSms ? testTarget.dest : "", campaign_name: "Templates test" }
                : { campaign_name: "Templates test" };
            const payload = isSms
                ? { phone: testTarget.dest, body: testTarget.row.body, variables }
                : { email: testTarget.dest, template_id: testTarget.row.id,
                    subject: testTarget.row.subject || undefined,
                    variables, lead_id: leadIdForEmail || undefined };
            const { data } = await api.post(url, payload);
            setTestTarget(t => ({ ...t, busy: false, result: data }));
            toast.success(data.mocked ? `Test ${isSms ? "SMS" : "email"} sent (mocked)` : `Test ${isSms ? "SMS" : "email"} sent (LIVE)`);
        } catch (e) {
            setTestTarget(t => ({ ...t, busy: false }));
            toast.error(formatApiError(e));
        }
    };

    // Filter Provider Route options by the currently-selected channel.
    const routesForChannel = useMemo(() => {
        const ch = editor?.row?.channel;
        if (!ch) return [];
        return (routes || []).filter(r => r.channel === ch);
    }, [routes, editor?.row?.channel]);

    return (
        <div className="space-y-6" data-testid="templates-panel">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <h1 className="text-lg md:text-xl font-semibold tracking-tight">Message Templates</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Reusable SMS &amp; email templates. Add Workflow Eligibility on client-workflow templates so they surface in Workflow Automation.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <div className="inline-flex border border-border rounded-md overflow-hidden">
                        {["all", "sms", "email"].map(v => (
                            <button
                                key={v}
                                onClick={() => setChannelFilter(v)}
                                data-testid={`channel-filter-${v}`}
                                className={`px-3 py-1.5 text-xs capitalize ${channelFilter === v ? "bg-primary/10 text-primary" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                            >{v}</button>
                        ))}
                    </div>
                    <div className="inline-flex border border-border rounded-md overflow-hidden">
                        {["all", "workflow_ready"].map(v => (
                            <button
                                key={v}
                                onClick={() => setEligibilityFilter(v)}
                                data-testid={`elig-filter-${v}`}
                                className={`px-3 py-1.5 text-xs ${eligibilityFilter === v ? "bg-primary/10 text-primary" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                            >{v === "all" ? "All" : "Workflow ready"}</button>
                        ))}
                    </div>
                    <div className="inline-flex border border-border rounded-md overflow-hidden">
                        {["active", "archived"].map(v => (
                            <button
                                key={v}
                                onClick={() => setFilter(v)}
                                data-testid={`filter-${v}`}
                                className={`px-3 py-1.5 text-xs capitalize ${filter === v ? "bg-primary/10 text-primary" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                            >{v}</button>
                        ))}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setEditor({ mode: "create", row: empty("sms") })} data-testid="new-sms-template-button">
                        <Plus className="h-4 w-4 mr-1" /> SMS
                    </Button>
                    <Button size="sm" onClick={() => setEditor({ mode: "create", row: empty("email") })} data-testid="new-email-template-button">
                        <Plus className="h-4 w-4 mr-1" /> Email
                    </Button>
                </div>
            </div>

            {/* Picker diagnostics collapsible */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 flex flex-col md:flex-row md:items-center gap-3" data-testid="picker-diagnostics-bar">
                <Bug className="h-4 w-4 text-slate-500" />
                <div className="text-xs text-slate-600 flex-1">
                    Diagnose why a template does or does not appear in a Workflow picker (Send SMS / Send Email actions).
                </div>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => runDiagnostics("sms")} data-testid="diag-workflow-sms">Workflow SMS</Button>
                    <Button size="sm" variant="outline" onClick={() => runDiagnostics("email")} data-testid="diag-workflow-email">Workflow Email</Button>
                </div>
            </div>
            {diag && (
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="picker-diagnostics-result">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-sm">
                            Picker: <code className="font-mono text-xs bg-slate-100 px-1 rounded">{diag.picker}</code>
                            <span className="mx-2 text-slate-400">·</span>
                            <span className="text-slate-700">{diag.total_included} of {diag.total_templates_scanned} templates qualify</span>
                            <span className="mx-2 text-slate-400">·</span>
                            <span className="text-slate-700">{diag.total_active_routes_for_channel} active {diag.required_channel.toUpperCase()} route(s)</span>
                        </div>
                        <button className="text-xs text-slate-500 hover:underline" onClick={() => setDiag(null)}>Close</button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="text-slate-500">
                                <tr className="text-left border-b border-slate-200">
                                    <th className="py-1.5 pr-3">Template</th>
                                    <th className="py-1.5 pr-2">Ch</th>
                                    <th className="py-1.5 pr-2">Aud</th>
                                    <th className="py-1.5 pr-2">Purpose</th>
                                    <th className="py-1.5 pr-2">Active</th>
                                    <th className="py-1.5 pr-2">Appr</th>
                                    <th className="py-1.5 pr-2">Route</th>
                                    <th className="py-1.5 pr-2">Included</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(diag.rows || []).slice(0, 100).map(r => (
                                    <tr key={r.id} className="border-b border-slate-100">
                                        <td className="py-1.5 pr-3 font-mono truncate max-w-[200px]" title={r.template_key}>{r.template_name || r.template_key}</td>
                                        <td className="py-1.5 pr-2">{r.checks.channel_matches ? "✓" : "✗"}</td>
                                        <td className="py-1.5 pr-2">{r.checks.audience_client_or_both ? "✓" : "✗"}</td>
                                        <td className="py-1.5 pr-2">{r.checks.purpose_client_workflow ? "✓" : "✗"}</td>
                                        <td className="py-1.5 pr-2">{r.checks.active ? "✓" : "✗"}</td>
                                        <td className="py-1.5 pr-2">{r.checks.approved_not_false ? "✓" : "✗"}</td>
                                        <td className="py-1.5 pr-2">{r.checks.provider_route_linked_and_active ? "✓" : "✗"}</td>
                                        <td className="py-1.5 pr-2">{r.included ? <span className="text-green-700 font-medium">YES</span> : <span className="text-slate-500">no</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Empty state when no active workflow-ready SMS routes at all. */}
            {routes.length > 0 && !routes.some(r => r.channel === "sms" && r.active) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 flex items-center gap-3" data-testid="no-active-sms-routes-warning">
                    <Info className="h-4 w-4 text-amber-700 shrink-0" />
                    <div className="flex-1 text-xs text-amber-900">
                        No active Twilio SMS Messaging Routes exist. Workflow SMS templates cannot be marked eligible until you add or activate a route.
                    </div>
                    <a
                        href="/settings"
                        onClick={(e) => { e.preventDefault(); window.location.hash = ""; window.history.pushState(null, "", "/settings#messaging-routes"); window.dispatchEvent(new HashChangeEvent("hashchange")); }}
                        className="text-xs font-medium text-amber-900 underline"
                    >Open Messaging Routes</a>
                </div>
            )}

            <div className="bg-white border border-border rounded-xl shadow-sm overflow-hidden">
                {items.length === 0 ? (
                    <div className="p-12 text-center text-sm text-muted-foreground">No {filter} templates.</div>
                ) : (
                    <div className="divide-y divide-border">
                        {items.map(row => {
                            const linkedRoute = row.provider_route_id ? routeMap[row.provider_route_id] : null;
                            const elig = evaluateEligibility(row, linkedRoute);
                            return (
                                <div key={row.id} className="px-5 py-4 flex flex-col md:flex-row md:items-start gap-4" data-testid={`template-row-${row.template_key}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <code className="text-xs bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded font-mono">{row.template_key}</code>
                                            <span className="text-sm font-medium text-gray-900">{row.template_name}</span>
                                            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${row.channel === "email" ? "bg-purple-50 text-purple-700" : "bg-blue-50 text-blue-700"}`}>{row.channel}</span>
                                            {row.audience && (
                                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{row.audience}</span>
                                            )}
                                            {row.purpose && (
                                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-mono" title="Purpose">{row.purpose}</span>
                                            )}
                                            {row.approved === true && (
                                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 inline-flex items-center gap-0.5" data-testid={`approved-${row.template_key}`}>
                                                    <ShieldCheck className="h-3 w-3" /> approved
                                                </span>
                                            )}
                                            {row.approved === false && (
                                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-50 text-rose-700">not approved</span>
                                            )}
                                            {!row.active && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Inactive</span>}
                                            {row.archived && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Archived</span>}
                                            {row.category && (
                                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">{row.category}</span>
                                            )}
                                            {row.purpose === "client_workflow" && elig.passed && (
                                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-green-100 text-green-800" data-testid={`workflow-ready-${row.template_key}`}>Workflow-ready</span>
                                            )}
                                            {row.purpose === "client_workflow" && !elig.passed && (
                                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid={`workflow-blocked-${row.template_key}`}>Workflow blocked</span>
                                            )}
                                            {row.channel === "email" && isPlaceholderSendgridId(row.sendgrid_template_id) && (
                                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-50 text-rose-700" data-testid={`placeholder-${row.template_key}`} title="Replace the SendGrid Template ID before sending live email">
                                                    Placeholder SG ID
                                                </span>
                                            )}
                                        </div>
                                        {row.channel === "sms" ? (
                                            <div className="text-xs text-gray-600 mt-1 line-clamp-2 whitespace-pre-wrap">{row.body}</div>
                                        ) : (
                                            <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                                <span>SG: <code className="font-mono text-[11px]">{row.sendgrid_template_id || "—"}</code></span>
                                                {row.subject && <span>Subject: {row.subject}</span>}
                                                <span className="text-muted-foreground">
                                                    vars: <code className="font-mono text-[11px]">{EMAIL_DTD_KEYS.map(k => `{{${k}}}`).join(" ")}</code>
                                                </span>
                                            </div>
                                        )}
                                        {row.provider_route_id && !(row.channel === "sms" && row.send_method === "exact_from_number") && (
                                            <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1" data-testid={`route-${row.template_key}`}>
                                                Route: <span className="font-medium text-slate-700">{row.provider_route_name || linkedRoute?.name || row.provider_route_id}</span>
                                                {linkedRoute && !linkedRoute.active && <span className="text-amber-700 ml-1">(inactive)</span>}
                                            </div>
                                        )}
                                        {/* P1B.1 — sender label (SMS templates only). */}
                                        {row.channel === "sms" && (
                                            <div
                                                className="text-[11px] mt-1 flex items-center gap-1"
                                                data-testid={`sender-label-${row.template_key}`}
                                                data-send-method={row.send_method || "messaging_service"}
                                            >
                                                <Phone className="h-3 w-3 text-slate-500" />
                                                {row.send_method === "exact_from_number" ? (
                                                    row.exact_from_number ? (
                                                        <span className="text-slate-700">
                                                            Exact From: <span className="font-medium">{row.exact_from_number}</span>
                                                            <span className="ml-1 text-amber-700 text-[10px]" data-testid={`sender-not-live-${row.template_key}`}>
                                                                (not yet active in live sending)
                                                            </span>
                                                        </span>
                                                    ) : (
                                                        <span className="text-rose-700 font-medium" data-testid={`sender-invalid-${row.template_key}`}>
                                                            Exact From selected but no number set
                                                        </span>
                                                    )
                                                ) : (
                                                    <span className="text-slate-700">
                                                        Messaging Service: <span className="font-medium">{row.provider_route_name || linkedRoute?.name || "— none —"}</span>
                                                        {(!row.provider_route_id) && (
                                                            <span className="ml-1 text-amber-700 text-[10px]">(no route)</span>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-1 flex-wrap">
                                        <Button variant="ghost" size="icon" onClick={() => preview(row)} title="Preview" data-testid={`preview-${row.template_key}`}>
                                            <Eye className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => setTestTarget({ open: true, dest: "", row, busy: false, result: null, sampleFirstName: "" })} title="Send test" data-testid={`test-${row.template_key}`}>
                                            <Send className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => setEditor({ mode: "edit", row: { ...row, subject: row.subject || "", compliance_notes: row.compliance_notes || "" } })} title="Edit" data-testid={`edit-${row.template_key}`}>
                                            <Edit3 className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => duplicate(row)} title="Duplicate" data-testid={`duplicate-${row.template_key}`}>
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => archive(row, !row.archived)} title={row.archived ? "Unarchive" : "Archive"} data-testid={`archive-${row.template_key}`}>
                                            {row.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => setConfirm({ title: `Delete "${row.template_name}"?`, body: "Hard delete — only allowed if no campaign references this template. Otherwise archive instead.", run: () => remove(row) })} title="Delete" data-testid={`delete-${row.template_key}`}>
                                            <Trash2 className="h-4 w-4 text-rose-500" />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Editor dialog */}
            <Dialog open={!!editor} onOpenChange={(o) => { if (!o) setEditor(null); }}>
                <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="template-editor-dialog">
                    <DialogHeader>
                        <DialogTitle>{editor?.mode === "edit" ? "Edit template" : "New template"}</DialogTitle>
                        <DialogDescription>
                            Supported merge variables: <code className="text-xs">{SUPPORTED_VARIABLES.map(v => `{{${v}}}`).join(" ")}</code>
                        </DialogDescription>
                    </DialogHeader>
                    {editor && (() => {
                        const linkedRoute = editor.row.provider_route_id ? routeMap[editor.row.provider_route_id] : null;
                        const elig = evaluateEligibility(editor.row, linkedRoute);
                        return (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Template key</Label>
                                        <Input
                                            value={editor.row.template_key}
                                            onChange={e => setEditor({ ...editor, row: { ...editor.row, template_key: e.target.value.replace(/[^a-z0-9_-]/gi, "_").toLowerCase() } })}
                                            placeholder="nsf_01"
                                            data-testid="editor-key"
                                            disabled={editor.mode === "edit" && (editor.row.template_key || "").startsWith("twilio_")}
                                        />
                                        <p className="text-[11px] text-muted-foreground mt-1">Snake_case, used by API &amp; legacy refs.</p>
                                    </div>
                                    <div>
                                        <Label>Display name</Label>
                                        <Input
                                            value={editor.row.template_name}
                                            onChange={e => setEditor({ ...editor, row: { ...editor.row, template_name: e.target.value } })}
                                            placeholder="NSF Recovery v1"
                                            data-testid="editor-name"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <Label>Channel</Label>
                                        <select
                                            value={editor.row.channel}
                                            onChange={e => setEditor({ ...editor, row: { ...editor.row, channel: e.target.value, provider_route_id: null, provider_route_name: null } })}
                                            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                                            data-testid="editor-channel"
                                        >
                                            <option value="sms">SMS</option>
                                            <option value="email">Email</option>
                                        </select>
                                    </div>
                                    <div>
                                        <Label>Category</Label>
                                        <Input
                                            value={editor.row.category || ""}
                                            onChange={e => setEditor({ ...editor, row: { ...editor.row, category: e.target.value.toUpperCase() } })}
                                            placeholder={editor.row.channel === "email" ? "LEADS" : "PAST_DUE"}
                                            data-testid="editor-category"
                                        />
                                    </div>
                                    <div className="flex items-center gap-3 pt-6">
                                        <Switch checked={editor.row.active} onCheckedChange={v => setEditor({ ...editor, row: { ...editor.row, active: v } })} data-testid="editor-active" />
                                        <Label className="cursor-pointer">Active</Label>
                                    </div>
                                </div>

                                {/* ─── Workflow Eligibility (W2C-1.6.1) ────────────── */}
                                <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-3" data-testid="editor-workflow-eligibility">
                                    <div>
                                        <div className="text-sm font-semibold text-indigo-900">Workflow Eligibility</div>
                                        <p className="text-[11px] text-indigo-800/80">
                                            Configure these so this template appears in the Workflow Automation picker (Send SMS / Send Email actions).
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                            <Label>Audience</Label>
                                            <select
                                                value={editor.row.audience || ""}
                                                onChange={e => setEditor({ ...editor, row: { ...editor.row, audience: e.target.value || null } })}
                                                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                                                data-testid="editor-audience"
                                            >
                                                <option value="">— unset (legacy) —</option>
                                                {AUDIENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <Label>Purpose</Label>
                                            <select
                                                value={editor.row.purpose || ""}
                                                onChange={e => setEditor({ ...editor, row: { ...editor.row, purpose: e.target.value || null } })}
                                                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                                                data-testid="editor-purpose"
                                            >
                                                <option value="">— unset —</option>
                                                {PURPOSE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="flex items-center gap-3 pt-6">
                                            <Switch
                                                checked={editor.row.approved === true}
                                                onCheckedChange={v => setEditor({ ...editor, row: { ...editor.row, approved: v ? true : false } })}
                                                data-testid="editor-approved"
                                            />
                                            <Label className="cursor-pointer">Approved for use</Label>
                                        </div>
                                    </div>
                                    <div>
                                        {editor.row.channel === "sms" ? (
                                            <SmsSenderMethodEditor
                                                row={editor.row}
                                                setRow={(patch) => setEditor({ ...editor, row: { ...editor.row, ...patch } })}
                                                routesForChannel={routesForChannel}
                                                routeMap={routeMap}
                                            />
                                        ) : (
                                            // 2026-02 P0/P1 — Email templates
                                            // no longer require a Provider
                                            // Route. Sender identity is on
                                            // the template itself + a
                                            // global-settings fallback. The
                                            // Sender / From configuration
                                            // for email lives in the Email
                                            // Send Mode + From Email / From
                                            // Name inputs further down.
                                            <div
                                                className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-[11px] text-slate-600"
                                                data-testid="email-no-provider-route-required"
                                            >
                                                Email templates use the SendGrid Dynamic Template (or Local subject + body) and the From Email / From Name configured below. No Messaging Route needed.
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <Label>Compliance notes (optional)</Label>
                                        <Textarea
                                            rows={2}
                                            value={editor.row.compliance_notes || ""}
                                            onChange={e => setEditor({ ...editor, row: { ...editor.row, compliance_notes: e.target.value } })}
                                            placeholder="TCPA basis, opt-in source, template review reference…"
                                            data-testid="editor-compliance-notes"
                                        />
                                    </div>

                                    {/* Eligibility checklist */}
                                    <div className="border-t border-indigo-200 pt-3">
                                        <div className="text-xs font-medium text-indigo-900 mb-1.5">
                                            Will this show in the Workflow {editor.row.channel === "sms" ? "SMS" : "Email"} picker?
                                            {" "}
                                            {elig.passed
                                                ? <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-700"><ShieldCheck className="h-3 w-3" /> Yes</span>
                                                : <span className="ml-1 text-amber-700">Not yet</span>}
                                        </div>
                                        <ul className="text-[11px] space-y-0.5" data-testid="eligibility-checklist">
                                            {elig.checks.map(c => (
                                                <li key={c.key} data-testid={`elig-${c.key}`} className={c.ok ? "text-emerald-700" : "text-amber-700"}>
                                                    {c.ok ? "✓" : "○"} {c.label}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                {editor.row.channel === "email" ? (
                                    <>
                                        {/* ─── W2C-1.7.1 — Email Send Mode ─── */}
                                        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="editor-email-send-mode">
                                            <div>
                                                <div className="text-sm font-semibold text-slate-900">Email Send Mode</div>
                                                <p className="text-[11px] text-slate-600">Local = subject + body live here in A+ CaseFlow. Dynamic Template = pick a SendGrid dynamic template; body lives in SendGrid.</p>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <label className={`cursor-pointer rounded-md border px-3 py-2 flex items-start gap-2 text-sm transition ${editor.row.email_send_mode !== "dynamic_template" ? "border-sky-400 bg-white ring-1 ring-sky-200" : "border-slate-200 bg-white/60"}`}>
                                                    <input
                                                        type="radio"
                                                        name="email_send_mode"
                                                        checked={editor.row.email_send_mode !== "dynamic_template"}
                                                        onChange={() => setEditor({ ...editor, row: { ...editor.row, email_send_mode: "local" } })}
                                                        data-testid="editor-email-mode-local"
                                                        className="mt-0.5"
                                                    />
                                                    <div>
                                                        <div className="font-medium text-slate-900">Local CaseFlow Template</div>
                                                        <div className="text-[11px] text-slate-600">Subject + body rendered in A+ CaseFlow. No SendGrid template ID needed.</div>
                                                    </div>
                                                </label>
                                                <label className={`cursor-pointer rounded-md border px-3 py-2 flex items-start gap-2 text-sm transition ${editor.row.email_send_mode === "dynamic_template" ? "border-sky-400 bg-white ring-1 ring-sky-200" : "border-slate-200 bg-white/60"}`}>
                                                    <input
                                                        type="radio"
                                                        name="email_send_mode"
                                                        checked={editor.row.email_send_mode === "dynamic_template"}
                                                        onChange={() => setEditor({ ...editor, row: { ...editor.row, email_send_mode: "dynamic_template" } })}
                                                        data-testid="editor-email-mode-dynamic"
                                                        className="mt-0.5"
                                                    />
                                                    <div>
                                                        <div className="font-medium text-slate-900">SendGrid Dynamic Template</div>
                                                        <div className="text-[11px] text-slate-600">Pick a template synced from SendGrid; body is managed in SendGrid.</div>
                                                    </div>
                                                </label>
                                            </div>
                                        </div>

                                        {editor.row.email_send_mode === "dynamic_template" ? (
                                            <div className="space-y-3" data-testid="editor-dynamic-template-section">
                                                <div>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <Label className="mb-0">SendGrid Dynamic Template</Label>
                                                        <Button size="sm" variant="outline" onClick={syncSendGrid} disabled={sgSyncing} data-testid="editor-sg-sync">
                                                            {sgSyncing ? "Syncing…" : "Sync from SendGrid"}
                                                        </Button>
                                                    </div>
                                                    {sgTemplates.length > 0 ? (
                                                        <select
                                                            value={editor.row.sendgrid_template_id || ""}
                                                            onChange={(e) => setEditor({ ...editor, row: { ...editor.row, sendgrid_template_id: e.target.value || null } })}
                                                            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm font-mono"
                                                            data-testid="editor-sg-dropdown"
                                                        >
                                                            <option value="">— select a template —</option>
                                                            {sgTemplates.map(t => (
                                                                <option key={t.sendgrid_template_id} value={t.sendgrid_template_id}>
                                                                    {t.name} · {(t.sendgrid_template_id || "").slice(0, 10)}…{(t.sendgrid_template_id || "").slice(-4)}{t.synced_at ? ` · synced ${new Date(t.synced_at).toLocaleDateString()}` : ""}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <div className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 text-xs text-slate-600 flex items-center gap-2" data-testid="editor-sg-empty">
                                                            <Info className="h-3.5 w-3.5 text-slate-500" />
                                                            No SendGrid dynamic templates synced yet. Click <span className="font-medium">Sync from SendGrid</span> above. The SendGrid API key is read from Settings → Providers — it is never asked for here.
                                                        </div>
                                                    )}
                                                </div>
                                                {editor.row.sendgrid_template_id && (
                                                    <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 flex items-center gap-2" data-testid="editor-sg-selected">
                                                        <ShieldCheck className="h-3.5 w-3.5" />
                                                        Selected: <code className="font-mono">{editor.row.sendgrid_template_id}</code>
                                                    </div>
                                                )}
                                                <div>
                                                    <button
                                                        type="button"
                                                        className="text-[11px] text-slate-500 underline"
                                                        onClick={() => setShowAdvancedId(v => !v)}
                                                        data-testid="editor-advanced-id-toggle"
                                                    >
                                                        {showAdvancedId ? "Hide" : "Advanced: manually enter template ID"}
                                                    </button>
                                                    {showAdvancedId && (
                                                        <Input
                                                            value={editor.row.sendgrid_template_id || ""}
                                                            onChange={e => setEditor({ ...editor, row: { ...editor.row, sendgrid_template_id: e.target.value } })}
                                                            placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxxxx"
                                                            data-testid="editor-sendgrid-id"
                                                            className="font-mono text-sm mt-1"
                                                        />
                                                    )}
                                                </div>
                                                <div>
                                                    <Label>Subject / Body (optional admin notes only)</Label>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <Input value={editor.row.subject || ""} onChange={e => setEditor({ ...editor, row: { ...editor.row, subject: e.target.value } })} placeholder="(optional) subject note" data-testid="editor-subject" />
                                                    </div>
                                                    <p className="text-[11px] text-slate-500 mt-1">Not authoritative — the actual subject/body lives in SendGrid.</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-3" data-testid="editor-local-template-section">
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="col-span-2">
                                                        <Label>Subject</Label>
                                                        <Input value={editor.row.subject || ""} onChange={e => setEditor({ ...editor, row: { ...editor.row, subject: e.target.value } })} placeholder="Hi {first_name}, …" data-testid="editor-subject" />
                                                    </div>
                                                    <div>
                                                        <Label>From Email fallback</Label>
                                                        <Input value={editor.row.from_email || ""} onChange={e => setEditor({ ...editor, row: { ...editor.row, from_email: e.target.value } })} placeholder="(uses settings default)" data-testid="editor-from-email" />
                                                    </div>
                                                    <div>
                                                        <Label>From Name fallback</Label>
                                                        <Input value={editor.row.from_name || ""} onChange={e => setEditor({ ...editor, row: { ...editor.row, from_name: e.target.value } })} placeholder="(uses settings default)" data-testid="editor-from-name" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <Label>Body</Label>
                                                    <Textarea
                                                        rows={6}
                                                        value={editor.row.body}
                                                        onChange={e => setEditor({ ...editor, row: { ...editor.row, body: e.target.value } })}
                                                        placeholder="Hi {first_name}, …"
                                                        data-testid="editor-body"
                                                        className="font-mono text-sm"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div>
                                        <Label>Body</Label>
                                        <Textarea
                                            rows={5}
                                            value={editor.row.body}
                                            onChange={e => setEditor({ ...editor, row: { ...editor.row, body: e.target.value } })}
                                            placeholder="Hi {first_name}, this is {dept_processor}…"
                                            data-testid="editor-body"
                                            className="font-mono text-sm"
                                        />
                                    </div>
                                )}

                                {/* ─── Rendered Preview (W2C-1.7) ────────────── */}
                                <RenderedPreview
                                    preview={renderPreview}
                                    channel={editor.row.channel}
                                />
                            </div>
                        );
                    })()}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditor(null)}>Cancel</Button>
                        <Button onClick={save} data-testid="editor-save">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Preview dialog */}
            <Dialog open={!!previewing} onOpenChange={(o) => { if (!o) setPreviewing(null); }}>
                <DialogContent className="max-w-xl" data-testid="template-preview-dialog">
                    <DialogHeader>
                        <DialogTitle>Preview · {previewing?.template_name}</DialogTitle>
                        <DialogDescription>Rendered with sample lead values.</DialogDescription>
                    </DialogHeader>
                    {previewing && (
                        <div className="space-y-3">
                            {previewing.subject && (
                                <div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Subject</div>
                                    <div className="text-sm font-medium text-gray-900 mt-0.5">{previewing.subject}</div>
                                </div>
                            )}
                            <div>
                                <div className="text-xs text-muted-foreground uppercase tracking-wider">Body</div>
                                <pre className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-900 bg-gray-50 border border-border rounded-md p-3 font-mono">{previewing.body}</pre>
                            </div>
                            <div>
                                <div className="text-xs text-muted-foreground uppercase tracking-wider">Sample variables</div>
                                <pre className="mt-1 text-xs text-gray-700 font-mono">{JSON.stringify(previewing.variables_used, null, 2)}</pre>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Send test dialog */}
            <Dialog open={testTarget.open} onOpenChange={(o) => { if (!o) setTestTarget({ open: false, dest: "", row: null, busy: false, result: null, sampleFirstName: "" }); }}>
                <DialogContent className="max-w-md" data-testid="template-test-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            Send test {testTarget.row?.channel === "email" ? "email" : "SMS"} · {testTarget.row?.template_name}
                        </DialogTitle>
                        <DialogDescription>
                            {testTarget.row?.channel === "email"
                                ? "Sends a real SendGrid Dynamic Template email to the recipient. Honors global Test Mode."
                                : "Sends the rendered body to the number you enter. Honors global Test Mode."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>{testTarget.row?.channel === "email" ? "Recipient email" : "Recipient phone"}</Label>
                            <Input
                                value={testTarget.dest}
                                onChange={e => setTestTarget({ ...testTarget, dest: e.target.value })}
                                placeholder={testTarget.row?.channel === "email" ? "you@example.com" : "+15555550100"}
                                data-testid="template-test-dest"
                            />
                        </div>
                        <div>
                            <Label>Sample first_name <span className="text-muted-foreground font-normal">(optional)</span></Label>
                            <Input
                                value={testTarget.sampleFirstName || ""}
                                onChange={e => setTestTarget({ ...testTarget, sampleFirstName: e.target.value })}
                                placeholder={testTarget.row?.channel === "email" ? "Empty = pull first_name from a real lead" : "e.g. Sam"}
                                data-testid="template-test-sample-first-name"
                            />
                        </div>
                        {testTarget.row?.channel === "email" && isPlaceholderSendgridId(testTarget.row?.sendgrid_template_id) && (
                            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                                Heads up — this template still has a placeholder SendGrid Template ID. Live sends will be blocked until you replace it.
                            </div>
                        )}
                        {testTarget.result && (
                            <div className="border border-border rounded-md bg-gray-50 p-2 text-xs font-mono overflow-auto max-h-56" data-testid="template-test-debug">
                                <pre className="whitespace-pre-wrap break-words">{JSON.stringify(testTarget.result, null, 2)}</pre>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTestTarget({ open: false, dest: "", row: null, busy: false, result: null, sampleFirstName: "" })}>Close</Button>
                        <Button onClick={sendTest} disabled={testTarget.busy} data-testid="template-test-send">
                            {testTarget.busy ? "Sending…" : "Send test"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirm delete */}
            <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
                        <AlertDialogDescription>{confirm?.body}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-rose-600 hover:bg-rose-700"
                            onClick={async () => { const fn = confirm?.run; setConfirm(null); if (fn) await fn(); }}
                            data-testid="confirm-delete-template"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}


/**
 * P1B.1 — SMS-only sender-method editor.
 *
 * Two radios:
 *   • Messaging Service — reuses the existing Provider Route picker.
 *     Legacy default; every existing SMS template lands here.
 *   • Exact From Number — dropdown of Twilio-owned, SMS-capable
 *     Incoming Phone Numbers pulled from
 *     `GET /api/admin/twilio-sender-numbers`. Non-SMS-capable rows
 *     are disabled so operators can't select a voice-only number.
 *
 * SAFETY: this component ONLY writes to `send_method` / `exact_from_number`
 * / `provider_route_id` fields on the template. It does NOT alter live
 * send behavior — the backend acknowledges the fields and the executor
 * ignores them until the next phase.
 */
function SmsSenderMethodEditor({ row, setRow, routesForChannel, routeMap }) {
    const method = row.send_method || "messaging_service";
    const [numbers, setNumbers] = useState(null);
    const [loadingNumbers, setLoadingNumbers] = useState(false);
    const [numbersError, setNumbersError] = useState(null);

    const loadNumbers = useCallback(async () => {
        setLoadingNumbers(true);
        setNumbersError(null);
        try {
            const { data } = await api.get("/admin/twilio-sender-numbers");
            if (data.error) setNumbersError(data.error);
            setNumbers(data.numbers || []);
        } catch (e) {
            setNumbersError(formatApiError(e) || "Failed to load numbers");
            setNumbers([]);
        } finally {
            setLoadingNumbers(false);
        }
    }, []);

    useEffect(() => {
        if (method === "exact_from_number" && numbers === null) loadNumbers();
    }, [method, numbers, loadNumbers]);

    const setMethod = (next) => {
        if (next === method) return;
        // Clear the OTHER method's data so the doc has a single source of
        // truth. Backend also enforces this on save; the frontend copy keeps
        // the UI honest so operators see exactly what will be saved.
        if (next === "messaging_service") {
            setRow({ send_method: next, exact_from_number: null, exact_from_number_sid: null });
        } else {
            setRow({ send_method: next });
        }
    };

    return (
        <div className="space-y-3" data-testid="sms-sender-method-editor">
            <div>
                <Label>Sending Method</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                    <label
                        className={`cursor-pointer rounded-md border px-3 py-2 flex items-start gap-2 text-sm transition ${method === "messaging_service" ? "border-sky-400 bg-white ring-1 ring-sky-200" : "border-slate-200 bg-white/60"}`}
                        data-testid="sender-method-ms-radio-wrap"
                    >
                        <input
                            type="radio"
                            name="send_method"
                            checked={method === "messaging_service"}
                            onChange={() => setMethod("messaging_service")}
                            className="mt-0.5"
                            data-testid="sender-method-messaging-service"
                        />
                        <div>
                            <div className="font-medium text-slate-900">Messaging Service</div>
                            <div className="text-[11px] text-slate-600">
                                Send via a Twilio Messaging Service (route). Twilio picks the outbound number from the service&apos;s pool.
                            </div>
                        </div>
                    </label>
                    <label
                        className={`cursor-pointer rounded-md border px-3 py-2 flex items-start gap-2 text-sm transition ${method === "exact_from_number" ? "border-sky-400 bg-white ring-1 ring-sky-200" : "border-slate-200 bg-white/60"}`}
                        data-testid="sender-method-exact-radio-wrap"
                    >
                        <input
                            type="radio"
                            name="send_method"
                            checked={method === "exact_from_number"}
                            onChange={() => setMethod("exact_from_number")}
                            className="mt-0.5"
                            data-testid="sender-method-exact-from-number"
                        />
                        <div>
                            <div className="font-medium text-slate-900">Exact From Number</div>
                            <div className="text-[11px] text-slate-600">
                                Send from a specific Twilio-owned phone number. Only account-owned, SMS-capable numbers appear.
                            </div>
                        </div>
                    </label>
                </div>
            </div>

            {method === "messaging_service" && (
                <div data-testid="sender-method-ms-picker">
                    <Label>Messaging Route</Label>
                    {routesForChannel.length > 0 ? (
                        <select
                            value={row.provider_route_id || ""}
                            onChange={e => {
                                const rid = e.target.value || null;
                                const r = rid ? routeMap[rid] : null;
                                setRow({
                                    provider_route_id: rid,
                                    provider_route_name: r?.name || null,
                                });
                            }}
                            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                            data-testid="editor-provider-route"
                        >
                            <option value="">— no route selected —</option>
                            {routesForChannel.map(r => (
                                <option key={r.id} value={r.id} disabled={!r.active}>
                                    {r.name} {r.active ? "" : "(inactive)"} {r.twilio_messaging_service_sid ? `· ${r.twilio_messaging_service_sid.slice(0, 8)}…` : ""}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <div className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 text-xs text-slate-600 flex items-center gap-2" data-testid="editor-no-routes">
                            <Info className="h-3.5 w-3.5 text-slate-500" />
                            No Twilio SMS routes yet.{" "}
                            <a
                                href="/settings"
                                onClick={(e) => { e.preventDefault(); window.location.href = "/settings#messaging-routes"; }}
                                className="underline font-medium"
                            >
                                Add one in Messaging Routes <ExternalLink className="inline h-3 w-3" />
                            </a>
                        </div>
                    )}
                </div>
            )}

            {method === "exact_from_number" && (
                <div data-testid="sender-method-exact-picker" className="space-y-2">
                    <div className="flex items-center justify-between">
                        <Label>Twilio Sender Number</Label>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={loadNumbers}
                            disabled={loadingNumbers}
                            data-testid="refresh-sender-numbers"
                        >
                            <RefreshCw className={`h-3.5 w-3.5 ${loadingNumbers ? "animate-spin" : ""}`} />
                            <span className="ml-1">Refresh</span>
                        </Button>
                    </div>
                    {numbersError && (
                        <div className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900" data-testid="sender-numbers-error">
                            {numbersError}
                        </div>
                    )}
                    {loadingNumbers && !numbers && (
                        <div className="text-xs text-slate-500">Loading Twilio-owned numbers…</div>
                    )}
                    {numbers && numbers.length === 0 && !loadingNumbers && (
                        <div className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 text-xs text-slate-600 flex items-center gap-2" data-testid="sender-numbers-empty">
                            <Info className="h-3.5 w-3.5 text-slate-500" />
                            No Incoming Phone Numbers found on the configured Twilio Account.
                        </div>
                    )}
                    {numbers && numbers.length > 0 && (
                        <select
                            value={row.exact_from_number || ""}
                            onChange={e => {
                                const num = e.target.value || null;
                                const match = num ? numbers.find(n => n.phone_number === num) : null;
                                setRow({
                                    exact_from_number: num,
                                    exact_from_number_sid: match?.phone_number_sid || null,
                                });
                            }}
                            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                            data-testid="editor-exact-from-number"
                        >
                            <option value="">— pick an owned SMS-capable number —</option>
                            {numbers.map(n => (
                                <option
                                    key={n.phone_number_sid}
                                    value={n.phone_number}
                                    disabled={!n.sms_capable}
                                    data-sms-capable={String(!!n.sms_capable)}
                                >
                                    {n.friendly_name || n.phone_number} · {n.phone_number}
                                    {n.sms_capable ? "" : " (no SMS capability)"}
                                </option>
                            ))}
                        </select>
                    )}
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 flex items-start gap-2" data-testid="exact-from-inactive-note">
                        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>
                            <strong>Exact From is configured</strong> but will become active after sending-engine support is deployed. Live campaigns still use the Messaging Service selected on the route.
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
